import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PRIORITY_BADGE, PRIORITY_ORDER, buildGaugeBackground, consignedStateUpdate, deriveKanbanFromInput, getRowClasses, purchasedStateUpdate } from '../../lib/replenishment'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import { useLocalStorageState } from '../../hooks/useLocalStorageState'
import ColumnVisibilityMenu from './ColumnVisibilityMenu'
import { COLUMNS, type SortKey } from './matrixColumns'
import type { FilialSuggestion } from '../../lib/filialNeeds'
import type { KanbanStatusOp, ProductRow } from '../../types'

interface Props {
  rows: ProductRow[]
  /** Quando true, exibe as colunas de consolidação (Necessidade Filial / Sugestão Total). */
  hasFiliais?: boolean
  /** Quebra da necessidade por SKU (para tooltip da coluna Necessidade Filial). */
  filialMap?: Map<string, FilialSuggestion>
}

const fmtNum = (n: number, digits = 0) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })

const fmtMoney = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function getSortValue(r: ProductRow, key: SortKey): string | number {
  switch (key) {
    case 'sku':                 return r.sku
    case 'name':                return r.name.toLowerCase()
    case 'supplier':            return r.suppliers?.name?.toLowerCase() ?? ''
    case 'current_stock':       return r.current_stock
    case 'lead_time':           return r.suppliers?.lead_time_days ?? 0
    case 'monthly_avg':         return r.monthly_avg
    case 'daily_avg':           return r.daily_avg
    case 'coverage_months':     return r.coverage_months
    case 'coverage_days':       return r.coverage_days
    case 'reorder_point':       return r.reorder_point
    case 'gap_to_buy':          return r.gap_to_buy
    case 'last_sale_at':        return r.last_sale_at ? new Date(r.last_sale_at).getTime() : 0
    case 'days_without_sale':   return r.days_without_sale ?? -1
    case 'cost':                return r.cost
    case 'purchase_suggestion': return r.purchase_suggestion
    case 'filial_suggestion':   return r.filial_suggestion
    case 'total_suggestion':    return r.total_suggestion
    case 'approved_qty':        return r.approved_qty
    case 'priority':            return PRIORITY_ORDER.indexOf(r.priority)
    case 'kanban_status':       return r.kanban_status
    case 'order_number':        return r.active_order_number ?? ''
    case 'order_date':          return r.active_order_date ? new Date(r.active_order_date).getTime() : 0
    case 'order_status':        return r.active_order_status ?? ''
  }
}

function remoteValue(r: ProductRow): string {
  // Produto consignado nunca exibe a letra 'C' — só número (ou vazio)
  if (r.is_consigned) {
    return r.approved_qty > 0 ? String(r.approved_qty) : ''
  }
  return r.approval_input ?? (r.approved_qty > 0 ? String(r.approved_qty) : '')
}

// ─── Linha memoizada ──────────────────────────────────────────────────
// O draft e o saving vivem aqui. Digitar não causa re-render do pai.
// React.memo só re-renderiza quando o objeto `row` muda de referência.

interface RowProps {
  row: ProductRow
  onInactivate: (id: string, name: string) => Promise<void>
  inactivating: boolean
  hasFiliais: boolean
  filialMap?: Map<string, FilialSuggestion>
}

const MatrixRow = memo(function MatrixRow({ row: r, onInactivate, inactivating, hasFiliais, filialMap }: RowProps) {
  // Inicializa do remoto APENAS na montagem. Depois disso o draft é a verdade
  // visível — evita o realtime sobrescrever a edição do usuário.
  const initialRemote = remoteValue(r)
  const [draft, setDraft] = useState(initialRemote)
  const [saving, setSaving] = useState(false)
  const [togglingConsigned, setTogglingConsigned] = useState(false)
  const debounceRef = useRef<number | null>(null)
  const { notify } = useToast()

  const remote = remoteValue(r)
  const visualStatus: KanbanStatusOp = r.is_consigned
    ? 'Consignado'
    : (draft.trim().toUpperCase() !== remote.trim().toUpperCase()
        ? deriveKanbanFromInput(draft).status
        : r.kanban_status)

  const commit = useCallback(async (raw: string) => {
    const { status, numericQty } = deriveKanbanFromInput(raw)
    const normalized = (raw ?? '').trim().toUpperCase()
    const remoteNow = remoteValue(r)
    if (normalized === remoteNow.trim().toUpperCase()) return
    setSaving(true)
    // Produto consignado: status fica congelado em 'Consignado', sem letra no input
    const update = r.is_consigned
      ? {
          approval_input: null,
          approved_qty: numericQty,
          kanban_status: 'Consignado' as KanbanStatusOp,
        }
      : {
          approval_input: normalized || null,
          approved_qty: numericQty,
          kanban_status: status,
        }
    const { error } = await supabase.from('products').update(update).eq('id', r.id)
    setSaving(false)
    if (error) {
      setDraft(remoteNow)
      notify('error', `Erro ao salvar: ${error.message}`)
    }
  }, [r, notify])

  function handleChange(value: string) {
    setDraft(value)
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => { commit(value) }, 500)
  }

  function handleBlur(value: string) {
    if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null }
    commit(value)
  }

  async function toggleConsigned(next: boolean) {
    if (next === r.is_consigned) return
    if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null }
    setTogglingConsigned(true)
    const update = next ? consignedStateUpdate() : purchasedStateUpdate()
    const { error } = await supabase.from('products').update(update).eq('id', r.id)
    setTogglingConsigned(false)
    if (error) { notify('error', `Erro ao salvar: ${error.message}`); return }
    setDraft('')
  }

  // Limpa o timer pendente se a linha desmontar (navegação durante digitação)
  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [])

  // Gauge proporcional substitui a cor sólida da linha (null = mantém sólida via getRowClasses,
  // p/ status manual com override e SemFornecedor). Texto ganha halo na cor do tema p/ ler sobre as faixas.
  const gaugeBg = buildGaugeBackground({
    priority: r.priority,
    coverage_days: r.coverage_days,
    lead_time: r.suppliers?.lead_time_days ?? null,
    kanban_status: visualStatus,
  })

  return (
    <tr
      className={`border-b border-gray-800/60 transition-colors ${
        gaugeBg
          ? ((r.priority === 'Médio' || r.priority === 'Alto')
              ? 'text-[#1a1a1a] [text-shadow:0_0_3px_rgba(255,255,255,0.7)]' // passam pela faixa amarela: texto escuro + halo claro
              : 'text-ink [text-shadow:0_0_4px_rgb(var(--surface))]')
          : getRowClasses(r.priority, visualStatus)
      }`}
      style={gaugeBg ? { background: gaugeBg } : undefined}
    >
      <td data-col="sku" className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.sku}</td>
      <td data-col="name" className="px-3 py-2 max-w-xs truncate" title={r.name}>{r.name}</td>
      <td data-col="supplier" className="px-3 py-2 max-w-[200px] truncate" title={r.suppliers?.name ?? ''}>
        {r.suppliers?.name ?? '—'}
      </td>
      <td data-col="current_stock" className="px-3 py-2 text-right tabular-nums font-medium">
        {r.current_stock > 0 && r.current_stock < 1 ? fmtNum(r.current_stock, 3) : fmtNum(r.current_stock)}
      </td>
      <td data-col="lead_time" className="px-3 py-2 text-right tabular-nums">{r.suppliers?.lead_time_days ?? '—'}</td>
      <td data-col="monthly_avg" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.monthly_avg, 1)}</td>
      <td data-col="daily_avg" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.daily_avg, 2)}</td>
      <td data-col="coverage_months" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.coverage_months, 2)}</td>
      <td data-col="coverage_days" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.coverage_days, 1)}</td>
      <td data-col="reorder_point" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.reorder_point, 1)}</td>
      <td data-col="gap_to_buy" className="px-3 py-2 text-right tabular-nums">{fmtNum(r.gap_to_buy, 1)}</td>
      <td data-col="last_sale_at" className="px-3 py-2 whitespace-nowrap text-xs">
        {r.encalhe && (
          <span
            className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-black/50 text-amber-300 ring-1 ring-amber-400/50 text-[10px] font-bold align-middle"
            title="Encalhe: com saldo e sem venda há mais de 90 dias"
          >
            ⚠ ENCALHE
          </span>
        )}
        {fmtDate(r.last_sale_at)}
      </td>
      <td data-col="days_without_sale" className="px-3 py-2 text-right tabular-nums">
        {r.days_without_sale != null ? r.days_without_sale : '—'}
      </td>
      <td data-col="cost" className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.cost)}</td>
      <td data-col="purchase_suggestion" className="px-3 py-2 text-right tabular-nums font-medium">{r.purchase_suggestion}</td>
      {hasFiliais && (
        <>
          <td
            data-col="filial_suggestion"
            className="px-3 py-2 text-right tabular-nums"
            title={
              (filialMap?.get(r.sku)?.breakdown ?? [])
                .map(b => `${b.filial_name}: ${b.qty}`)
                .join('\n') || 'Sem necessidade nas filiais'
            }
          >
            {r.filial_suggestion || '—'}
          </td>
          <td data-col="total_suggestion" className="px-3 py-2 text-right tabular-nums font-semibold">{r.total_suggestion}</td>
        </>
      )}
      <td data-col="approved_qty" className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          <input
            type="text"
            value={draft}
            onChange={e => handleChange(e.target.value)}
            onBlur={e => handleBlur(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') {
                if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null }
                setDraft(remote)
                ;(e.target as HTMLInputElement).blur()
              }
            }}
            placeholder="—"
            title="Salva automaticamente. Número (gera pedido) ou letra: S, C, RF, B"
            className={`w-20 bg-black/25 border rounded px-2 py-1 text-right tabular-nums text-sm focus:outline-none focus:border-white/70 ${
              saving ? 'border-yellow-400/60' : 'border-white/30'
            }`}
          />
          <div
            role="group"
            aria-label="Identidade do produto: consignado ou comprado"
            className="inline-flex rounded border border-white/20 overflow-hidden text-[10px] font-semibold"
          >
            <button
              type="button"
              onClick={() => toggleConsigned(true)}
              disabled={togglingConsigned || r.is_consigned}
              title="Marcar produto como Consignado (identidade permanente)"
              className={`px-1.5 py-0.5 transition-colors ${
                r.is_consigned
                  ? 'bg-[#FFC000] text-gray-900'
                  : 'bg-black/20 text-gray-300 hover:bg-black/40'
              } ${togglingConsigned ? 'opacity-60 cursor-wait' : ''}`}
            >
              CONSIG
            </button>
            <button
              type="button"
              onClick={() => toggleConsigned(false)}
              disabled={togglingConsigned || !r.is_consigned}
              title="Marcar produto como Comprado (identidade permanente)"
              className={`px-1.5 py-0.5 border-l border-white/20 transition-colors ${
                !r.is_consigned
                  ? 'bg-gray-200 text-gray-900'
                  : 'bg-black/20 text-gray-300 hover:bg-black/40'
              } ${togglingConsigned ? 'opacity-60 cursor-wait' : ''}`}
            >
              COMP
            </button>
          </div>
        </div>
      </td>
      <td data-col="priority" className="px-3 py-2 whitespace-nowrap">
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${PRIORITY_BADGE[r.priority]}`}>
          {r.priority}
        </span>
      </td>
      <td data-col="kanban_status" className="px-3 py-2 whitespace-nowrap text-xs">{visualStatus}</td>
      <td data-col="order_number" className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.active_order_number ?? '—'}</td>
      <td data-col="order_date" className="px-3 py-2 whitespace-nowrap text-xs">
        {r.active_order_date ? new Date(r.active_order_date).toLocaleDateString('pt-BR') : '—'}
      </td>
      <td data-col="order_status" className="px-3 py-2 whitespace-nowrap text-xs">{r.active_order_status ?? '—'}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <button
          onClick={() => onInactivate(r.id, r.name)}
          disabled={inactivating}
          className="text-[10px] px-2 py-0.5 bg-black/30 hover:bg-black/50 disabled:opacity-50 rounded text-white border border-white/20"
          title="Inativar produto"
        >
          {inactivating ? '...' : 'Inativar'}
        </button>
      </td>
    </tr>
  )
})

// ─── Tabela ───────────────────────────────────────────────────────────

export default function ReplenishmentMatrix({ rows, hasFiliais = false, filialMap }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('priority')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [inactivatingId, setInactivatingId] = useState<string | null>(null)
  const [hiddenList, setHiddenList] = useLocalStorageState<SortKey[]>('bx:matrix-hidden-cols:v1', [])
  const { notify } = useToast()

  const hidden = useMemo(() => new Set<SortKey>(hiddenList), [hiddenList])

  const availableColumns = useMemo(() => COLUMNS.filter(c => hasFiliais || !c.filialOnly), [hasFiliais])

  // CSS injetado: esconde via display:none as colunas ocultas sem precisar re-renderizar
  // as N linhas memoizadas (apenas o <style> re-renderiza ao toggle).
  const hideStyles = useMemo(() => {
    const keys = hiddenList.filter(k => !COLUMNS.find(c => c.key === k)?.alwaysVisible)
    if (keys.length === 0) return ''
    return keys
      .map(k => `.bx-matrix-table [data-col="${k}"]{display:none}`)
      .join('')
  }, [hiddenList])

  const setHidden = useCallback((next: Set<SortKey>) => {
    setHiddenList(Array.from(next))
  }, [setHiddenList])

  const handleInactivate = useCallback(async (id: string, name: string) => {
    if (!confirm(`Inativar "${name}"?\n\nO produto será removido do Painel BX e moverá para a aba Inativos.`)) return
    setInactivatingId(id)
    const { error } = await supabase.from('products').update({ is_active: false }).eq('id', id)
    setInactivatingId(null)
    if (error) notify('error', `Erro: ${error.message}`)
    else notify('success', `"${name}" inativado.`)
  }, [notify])

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = useMemo(() => {
    const list = [...rows]
    list.sort((a, b) => {
      const va = getSortValue(a, sortKey)
      const vb = getSortValue(b, sortKey)
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return list
  }, [rows, sortKey, sortDir])

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-ink-faint bg-surface border border-line rounded-xl">
        <div className="text-center">
          <p className="text-lg font-medium mb-1">Nenhum produto encontrado</p>
          <p className="text-sm">Ajuste os filtros para visualizar os produtos.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-surface border border-line rounded-xl">
      {hideStyles && <style>{hideStyles}</style>}
      <div className="flex items-center justify-end px-3 pt-3">
        <ColumnVisibilityMenu columns={availableColumns} hidden={hidden} onChange={setHidden} />
      </div>
      <div className="overflow-auto rounded-xl max-h-[calc(100vh-320px)]">
        <table className="bx-matrix-table w-full text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              {availableColumns.map((col) => {
                const active = sortKey === col.key
                return (
                  <th
                    key={col.key}
                    data-col={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`text-left text-xs font-semibold uppercase tracking-wider px-3 py-3 whitespace-nowrap cursor-pointer select-none transition-colors border-b border-line ${
                      active ? 'text-brand-700 dark:text-brand-300 bg-line' : 'text-ink-muted bg-sunken hover:bg-line'
                    }`}
                    title="Clique para ordenar"
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      <span className={`text-[10px] ${active ? 'opacity-100' : 'opacity-30'}`}>
                        {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                      </span>
                    </span>
                  </th>
                )
              })}
              <th className="text-left text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-3 whitespace-nowrap bg-sunken border-b border-line">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <MatrixRow
                key={r.id}
                row={r}
                onInactivate={handleInactivate}
                inactivating={inactivatingId === r.id}
                hasFiliais={hasFiliais}
                filialMap={filialMap}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
